// ──────────────────────────────────────────────────────────────────────
// D61 — Public Content Review Page
//
// Mounted at /share/post/:token — NO auth required. The client opens
// this link from their inbox to review a scheduled post, leave comments,
// and approve.
// ──────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle2, MapPin, Calendar, AlertTriangle, MessageCircle, Send } from 'lucide-react';
import { publicContentPostAPI } from '../lib/api';
import { toast } from 'sonner';

const PLATFORM_LABELS = { IG: 'Instagram', FB: 'Facebook', TikTok: 'TikTok', X: 'X', YouTube: 'YouTube', Threads: 'Threads', LinkedIn: 'LinkedIn', GMB: 'Google My Business' };

export default function PublicContentReviewPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [name, setName] = useState(() => localStorage.getItem('stellar-public-reviewer-name') || '');
  const [email, setEmail] = useState(() => localStorage.getItem('stellar-public-reviewer-email') || '');
  const [commentBody, setCommentBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [approving, setApproving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await publicContentPostAPI.get(token);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to load this content. The link may have expired.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const persistReviewer = () => {
    if (name) localStorage.setItem('stellar-public-reviewer-name', name);
    if (email) localStorage.setItem('stellar-public-reviewer-email', email);
  };

  const handleAddComment = async () => {
    if (!name.trim() || !email.trim()) { toast.error('Enter your name and email first'); return; }
    if (!commentBody.trim()) { toast.error('Write a comment first'); return; }
    setPosting(true);
    try {
      await publicContentPostAPI.addComment(token, { name: name.trim(), email: email.trim(), body: commentBody.trim() });
      persistReviewer();
      setCommentBody('');
      toast.success('Comment sent');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send comment');
    } finally {
      setPosting(false);
    }
  };

  const handleApprove = async () => {
    if (!name.trim() || !email.trim()) { toast.error('Enter your name and email first'); return; }
    setApproving(true);
    try {
      await publicContentPostAPI.approve(token, { name: name.trim(), email: email.trim() });
      persistReviewer();
      toast.success('Approved — thank you!');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-card border-red-500/30">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-3" />
            <h1 className="text-[16px] font-semibold text-foreground mb-2">Link unavailable</h1>
            <p className="text-[12px] text-muted-foreground/70">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { post, client, comments } = data;
  const dt = post.scheduled_for ? new Date(post.scheduled_for) : null;

  return (
    <div className="min-h-screen bg-background" data-testid="public-content-review">
      {/* Branded header */}
      <header className="border-b border-border/20 bg-card/40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gold uppercase tracking-wider">Stellar Productions</div>
            <h1 className="font-display text-[16px] font-semibold text-foreground">Content review · {client?.name || 'Client'}</h1>
          </div>
          {post.client_approved && (
            <span className="text-[10px] px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1" data-testid="public-approved-badge">
              <CheckCircle2 className="h-3 w-3" /> Approved
            </span>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Media gallery */}
        {(post.media || []).length > 0 && (
          <Card className="bg-card border-border/30 overflow-hidden">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {post.media.map((m) => (
                  <div key={m.id} className="aspect-square bg-foreground/[0.05]" data-testid={`public-media-${m.id}`}>
                    {m.kind === 'video' ? (
                      <video src={m.url} poster={m.thumbnail_url || undefined} controls preload="metadata" className="w-full h-full object-cover bg-black" />
                    ) : (
                      <img src={m.url} alt={m.filename || ''} loading="lazy" className="w-full h-full object-cover" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Meta + caption */}
        <Card className="bg-card border-border/30">
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground/70">
              {dt && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {dt.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
              {(post.platforms || []).map(p => (
                <span key={p} className="px-2 py-0.5 rounded bg-gold/10 text-gold border border-gold/20">{PLATFORM_LABELS[p] || p}</span>
              ))}
              {(post.target_locations || []).length > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px]"><MapPin className="h-3 w-3" /> {post.target_locations.map(l => l.location_name).join(', ')}</span>
              )}
            </div>
            <div className="whitespace-pre-wrap text-[13px] text-foreground/90" data-testid="public-post-caption">{post.caption || '(no caption)'}</div>
          </CardContent>
        </Card>

        {/* Reviewer identity */}
        {!post.client_approved && (
          <Card className="bg-card border-border/30">
            <CardContent className="p-4 space-y-3">
              <div className="text-[12px] font-medium text-foreground/80">Your details</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground/60">Name</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" data-testid="public-reviewer-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground/60">Email</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" data-testid="public-reviewer-email" />
                </div>
              </div>
              <Button onClick={handleApprove} disabled={approving || !name || !email} className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-white border-0" data-testid="public-approve-btn">
                {approving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Approve this post
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Comments */}
        <Card className="bg-card border-border/30">
          <CardContent className="p-4 space-y-3">
            <div className="text-[12px] font-medium text-foreground/80 flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" /> Comments ({comments.length})</div>
            <div className="space-y-2">
              {comments.length === 0 ? (
                <div className="text-[11px] text-muted-foreground/50 italic">No comments yet.</div>
              ) : comments.map(c => (
                <div key={c.id} className="text-[12px] bg-foreground/[0.03] border border-border/20 rounded p-2" data-testid={`public-comment-${c.id}`}>
                  <div className="flex items-center gap-1.5 mb-0.5 text-[10px]">
                    <span className={`px-1.5 py-0.5 rounded ${c.author_type === 'external_client' ? 'bg-blue-500/15 text-blue-300' : 'bg-gold/15 text-gold'}`}>
                      {c.author_type === 'external_client' ? 'Client' : 'Stellar team'}
                    </span>
                    <span className="font-medium text-foreground/90">{c.author_name}</span>
                    <span className="text-muted-foreground/40">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-foreground/80 whitespace-pre-wrap">{c.body}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-1.5">
              <Textarea value={commentBody} onChange={e => setCommentBody(e.target.value)} rows={2} placeholder="Add a comment…" className="text-[12px]" data-testid="public-comment-input" />
              <Button onClick={handleAddComment} disabled={posting || !commentBody.trim()} className="h-auto self-stretch px-3 bg-gold/15 hover:bg-gold/25 text-gold border-0" data-testid="public-comment-send">
                {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground/50">Your comment will be sent to the Stellar team managing this account.</p>
          </CardContent>
        </Card>

        <footer className="text-center text-[10px] text-muted-foreground/40 pt-4 pb-8">
          Powered by Stellar Productions CRM
        </footer>
      </main>
    </div>
  );
}
